import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { ScheduleEvent } from '@/types';
import { Calendar, MapPin, Trophy, Users } from 'lucide-react';
import { format } from 'date-fns';

export const Schedule: React.FC = () => {
  const [events, setEvents] = useState<ScheduleEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'past'>('upcoming');

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const eventsRef = collection(db, 'schedules');
        const q = query(
          eventsRef,
          where('programId', '==', 'willard-tigers'),
          orderBy('date', 'desc')
        );

        const snapshot = await getDocs(q);
        const eventsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate() || new Date(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        })) as ScheduleEvent[];

        setEvents(eventsData);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const now = new Date();
  const filteredEvents = events.filter(event => {
    if (filter === 'upcoming') return event.date >= now;
    if (filter === 'past') return event.date < now;
    return true;
  });

  // Group by month, in chronological order
  const sorted = [...filteredEvents].sort((a, b) => a.date.getTime() - b.date.getTime());
  const months: { label: string; events: ScheduleEvent[] }[] = [];
  for (const event of sorted) {
    const label = format(event.date, 'MMMM yyyy');
    const group = months[months.length - 1];
    if (group && group.label === label) {
      group.events.push(event);
    } else {
      months.push({ label, events: [event] });
    }
  }

  if (loading) {
    return (
      <div className="bg-willard-black text-white min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-willard-taupe"></div>
      </div>
    );
  }

  return (
    <div className="bg-willard-black text-white min-h-screen">
      <div className="max-w-6xl mx-auto px-5 py-14">
        {/* Page header */}
        <header className="mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-willard-taupe mb-2">
            2025-2026 Season
          </p>
          <h1 className="font-display text-5xl sm:text-6xl mb-3">SCHEDULE</h1>
          <p className="text-willard-grey-400 max-w-2xl">
            Matches, tournaments, and practices for Willard Tigers bowling.
          </p>
        </header>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-10">
          {[
            { value: 'upcoming', label: 'Upcoming' },
            { value: 'past', label: 'Past' },
            { value: 'all', label: 'All' },
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilter(tab.value as any)}
              className={`px-6 py-3 rounded-lg font-bold transition-colors ${
                filter === tab.value
                  ? 'bg-willard-taupe text-willard-black'
                  : 'border border-willard-grey-700 text-willard-grey-400 hover:border-willard-taupe'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Events grouped by month */}
        {months.length > 0 ? (
          <div className="space-y-10">
            {months.map((group) => (
              <section key={group.label}>
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-6 h-6 text-willard-taupe" />
                  <h2 className="font-heading text-3xl tracking-wide uppercase">
                    {group.label}
                  </h2>
                </div>
                <div className="grid gap-4">
                  {group.events.map((event) => (
                    <EventCard key={event.id} event={event} isPast={event.date < now} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="bg-willard-grey-900 rounded-xl border border-willard-grey-800 p-5 text-center py-14">
            <Calendar className="w-10 h-10 text-willard-grey-500 mx-auto mb-4" />
            <p className="text-willard-grey-400">
              Schedule coming soon. Fall season starts soon.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

interface EventCardProps {
  event: ScheduleEvent;
  isPast?: boolean;
}

const EventCard: React.FC<EventCardProps> = ({ event, isPast = false }) => {
  return (
    <div
      className={`bg-willard-grey-900 rounded-xl border border-willard-grey-800 p-5 ${
        isPast ? 'opacity-70' : ''
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Date block */}
        <div className="flex-shrink-0 w-16 text-center border-r border-willard-grey-800 pr-4">
          <div className="font-display text-3xl leading-none">
            {format(event.date, 'd')}
          </div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-willard-taupe mt-1">
            {format(event.date, 'MMM')}
          </div>
        </div>

        {/* Details */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-willard-taupe">
              {event.type}
            </span>
            <span
              className={`px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-[0.2em] ${
                event.isHome
                  ? 'bg-willard-taupe text-willard-black'
                  : 'border border-willard-grey-700 text-willard-grey-400'
              }`}
            >
              {event.isHome ? 'Home' : 'Away'}
            </span>
          </div>

          <h3 className="font-heading text-xl tracking-wide mb-2">{event.title}</h3>

          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-1 sm:gap-4 text-sm text-willard-grey-400">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-willard-taupe flex-shrink-0" />
              <span>{event.time}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-willard-taupe flex-shrink-0" />
              <span>{event.location}</span>
            </div>
            {event.opponent && (
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-willard-taupe flex-shrink-0" />
                <span>vs {event.opponent}</span>
              </div>
            )}
          </div>

          {event.description && (
            <p className="mt-2 text-sm text-willard-grey-400">{event.description}</p>
          )}

          {/* Result */}
          {event.result && (
            <div className="mt-3 flex items-center gap-3">
              <Trophy
                className={`w-4 h-4 ${
                  event.result.winner === 'home' ? 'text-green-500' : 'text-willard-grey-500'
                }`}
              />
              <span className="font-display text-lg">
                {event.result.homeScore} - {event.result.awayScore}
              </span>
              <span
                className={`text-[11px] font-bold uppercase tracking-[0.2em] ${
                  event.result.winner === 'home' ? 'text-green-500' : 'text-willard-grey-400'
                }`}
              >
                {event.result.winner === 'home'
                  ? 'Win'
                  : event.result.winner === 'away'
                  ? 'Loss'
                  : 'Tie'}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
